import React, { useState } from 'react';
import { connect } from 'react-redux';
import { AppState } from '@Redux/store';
import { Dispatch } from '@Redux/type';
import { UserPreferenceAction } from '@Redux/actions';
import { TYPES } from '@Types';
import { container } from '@Container';
import { Col, Row } from 'reactstrap';
import { presentProfilePresenter } from '@Selectors/profile/presentProfilePresenter';
import { FormattedMessage } from 'react-intl';
import { defaultMessages } from '@I18n/default';
import { MyClubsAction } from '@Redux/actions/federation/my-clubs.action';
import Repeater from '@Utils/Repeater';
import { MyClubsPresenter } from '@Selectors/federation/my-clubs.presenter';
import ClubNew from '@Components/federation/club/new/club.new';
import { Club } from '@Models';
import { ClubParams } from '@Components/federation/club/new/club.new.validate';
import { Link } from 'react-router-dom';
import GoogleMapComponent from '@Components/federation/google-map.component';
import { APIProvider } from '@vis.gl/react-google-maps';

const UserProfilPageIndex: React.FC<UserProfilPageIndexProps> = ({
  profile,
  clubs,
  load,
  editClub,
}) => {
  const [currentClub, setCurrentClub] = useState(null as Club | null);
  React.useEffect(() => {
    load();
  }, []);

  if (profile.isGuest) {
    return (
      <Row className="justify-content-center mt-5 mb-5">
        <Col xs={12}>
          <h1 className="text-center">
            You muse create an account if you want to have a profile
          </h1>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <div className="text-center m-3">
            <a
              href={`/${document.documentElement.lang}/users/sign_in`}
              className="btn btn-age btn-lg"
            >
              <FormattedMessage {...defaultMessages.usersSessionsNewSignIn} />
            </a>
          </div>
        </Col>
      </Row>
    );
  }

  return (
    <div>
      <Row className="justify-content-center mt-5 mb-5">
        <Col xs={12}>
          <h1 className="text-center">Hello {profile.name}</h1>
        </Col>
        <Col xs={12} md={6} className="text-center">
          <p>
            <FormattedMessage {...defaultMessages.userProfileFavoritGame} />{' '}
            <b>{profile.game}</b>.
          </p>
          <p>
            <FormattedMessage {...defaultMessages.userProfilePrefertUser} />{' '}
            <b>{profile.builderInterface}</b>&nbsp;
            <FormattedMessage
              {...defaultMessages.userProfileFavoritBuilderInterface}
            />
            .
          </p>
          <p>
            <FormattedMessage {...defaultMessages.userProfileUseLatestArmy} />{' '}
            <b>{profile.useLatest}</b>.
          </p>
          <p>
            <FormattedMessage {...defaultMessages.userProfileDefaultLocale} />{' '}
            <b>
              <FormattedMessage id={profile.defaultLocale} />
            </b>
            .
          </p>
          <div className="text-center m-3">
            <a
              href={`/${document.documentElement.lang}/users/edit`}
              className="btn btn-age btn-lg"
            >
              <i className="fa-solid fa-2x fa-pen-to-square"></i>
              <FormattedMessage {...defaultMessages.userProfileEdit} />
            </a>
          </div>
        </Col>
        <Col xs={12} md={6}>
          {currentClub && (
            <ClubNew
              club={currentClub}
              cancel={() => setCurrentClub(null)}
              handleSubmit={(v) => {
                editClub(currentClub, v);
                setCurrentClub(null);
              }}
            />
          )}
          {!currentClub && (
            <>
              <h2>Clubs</h2>
              {clubs.length > 0 && (
                <p>You are member of {clubs.length} clubs: </p>
              )}
              <div className="list-group">
                <Repeater data={clubs} indexPrefix={'club'}>
                  {(club: Club) => (
                    <div className="list-group-item list-group-item-action d-flex">
                      <span className="flex-grow-1">{club.name}</span>
                      {club.latitude && club.longitude && (
                        <Link
                          to={`/${document.documentElement.lang}/federation/clubs`}
                          className="btn btn-age p-1"
                        >
                          <i className="fa-solid fa-2x fa-map-location-dot"></i>
                        </Link>
                      )}
                      <button
                        className="btn btn-age p-1"
                        onClick={() => setCurrentClub(club)}
                      >
                        <i className="fa-solid fa-2x fa-edit"></i>
                      </button>
                    </div>
                  )}
                </Repeater>
              </div>
              <div className="text-center m-3">
                <button
                  className="btn btn-age btn-lg"
                  onClick={() => setCurrentClub({} as Club)}
                >
                  <i className="fa-solid fa-2x fa-plus"></i> Add a Club
                </button>
              </div>
            </>
          )}
        </Col>
        <Col xs={12}>
          <APIProvider apiKey={process.env.MAP_API_KEY ?? ''}>
            <GoogleMapComponent clubs={clubs.filter((club) => club.longitude && club.latitude)} players={[]} categories={[]} />
          </APIProvider>
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    profile: presentProfilePresenter(state),
    clubs: MyClubsPresenter(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  const action = container.get<UserPreferenceAction>(
    TYPES.Actions.User.UserPreferenceAction,
  );
  const myClubAction = container.get<MyClubsAction>(
    TYPES.Actions.Federation.MyClubsAction,
  );
  return {
    load: () => {
      dispatch(action.fetchData());
      dispatch(myClubAction.fetchData());
    },
    editClub: (club: Club, values: ClubParams) => {
      if (!club.id) dispatch(myClubAction.create(values));
      else dispatch(myClubAction.update(club.id, values));
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(UserProfilPageIndex);

type UserProfilPageIndexProps = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;
